import { useEffect, useRef } from 'react';
import type { Subscription } from 'centrifuge';
import { getCentrifuge } from './centrifuge';
import type {
  PublicationLike,
  RealtimeEnvelope,
  SubscriptionHandlers,
} from './types';

/**
 * Per-channel reference count. Centrifuge-js has no built-in pattern for
 * "unsubscribe when the last listener leaves" — repeatedly calling
 * .unsubscribe() tears down a channel another component is still using.
 * We own the counting to guarantee clean shutdown without premature tear-down.
 */
const refCounts = new Map<string, number>();

export function acquire(channel: string): number {
  const next = (refCounts.get(channel) ?? 0) + 1;
  refCounts.set(channel, next);
  return next;
}

export function release(channel: string): number {
  const next = Math.max(0, (refCounts.get(channel) ?? 0) - 1);
  if (next === 0) refCounts.delete(channel);
  else refCounts.set(channel, next);
  return next;
}

/**
 * Clear all per-channel refcounts. Called from disposeCentrifuge() on
 * sign-out so the next user doesn't inherit stale "already subscribed"
 * state — the new Centrifuge client has no subscriptions yet, so our
 * counter must agree.
 */
export function resetChannelRefCounts(): void {
  refCounts.clear();
}

/**
 * Subscribe to a Centrifugo channel for the lifetime of the calling
 * component. Automatically:
 *   - opens (or reuses) the app-wide socket via getCentrifuge()
 *   - creates a subscription the first time this channel is seen
 *   - wires the caller's handlers to publication/subscribed events
 *   - unsubscribes on unmount of the last listener so server-side
 *     counters stay clean
 *
 * Generic over the payload type so callers get typed handlers without
 * casting on every use.
 */
export function useChannel<T = unknown>(
  channel: string | null | undefined,
  handlers: SubscriptionHandlers<T>,
): void {
  // Keep handlers in a ref so we can call the latest version without
  // re-subscribing when the parent re-renders with a new closure.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!channel) return;

    const client = getCentrifuge();
    let sub: Subscription | null = client.getSubscription(channel);
    if (!sub) {
      sub = client.newSubscription(channel);
    }
    const refCount = acquire(channel);

    const onPublication = (ctx: PublicationLike<T>) => {
      handlersRef.current.onPublication?.(ctx.data as RealtimeEnvelope<T>);
    };
    const onSubscribed = () => handlersRef.current.onSubscribed?.();
    const onUnsubscribed = (ctx: { reason?: string } = {}) =>
      handlersRef.current.onUnsubscribed?.(ctx.reason);
    const onError = (ctx: { error?: Error | string | { message: string } }) => {
      const err = ctx?.error;
      if (err instanceof Error) {
        handlersRef.current.onError?.(err);
      } else if (typeof err === 'string') {
        handlersRef.current.onError?.(new Error(err));
      } else if (err && typeof err === 'object' && 'message' in err) {
        handlersRef.current.onError?.(new Error(String(err.message)));
      }
    };

    sub.on('publication', onPublication);
    sub.on('subscribed', onSubscribed);
    sub.on('unsubscribed', onUnsubscribed);
    sub.on('error', onError);

    // Only call .subscribe() on the very first listener. Subsequent
    // listeners attach to an already-subscribed channel. centrifuge-js
    // does NOT re-emit `subscribed` for listeners that attach after the
    // edge has already fired, so we emit it synthetically below when the
    // sub is already in the `subscribed` state — handlers that depend on
    // knowing "subscription is live" would otherwise hang waiting.
    if (refCount === 1 && sub.state !== 'subscribed') {
      sub.subscribe();
    } else if (sub.state === 'subscribed') {
      handlersRef.current.onSubscribed?.();
    }

    return () => {
      sub?.off('publication', onPublication);
      sub?.off('subscribed', onSubscribed);
      sub?.off('unsubscribed', onUnsubscribed);
      sub?.off('error', onError);
      const remaining = release(channel);
      if (remaining === 0 && sub) {
        sub.unsubscribe();
      }
    };
  }, [channel]);
}

/**
 * Test hook — clears per-channel ref counts between cases. Kept as an
 * alias of the public resetChannelRefCounts() so existing tests keep
 * working; new code should import the public name.
 */
export function __resetChannelRefCountsForTests(): void {
  resetChannelRefCounts();
}
